import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiX, FiCalendar, FiMapPin, FiCheck } from 'react-icons/fi';
import { useGetMyEventsInfinite, type UserEventItem } from '../../api/media';
import { MEDIA_FALLBACK_IMAGE_SRC } from '../../utils/mediaFallback';

interface EventsModalProps {
  onSelect: (event: {
    id: number;
    name: string;
    location?: string;
    start?: string;
    image?: string;
  }) => void;
  onClose: () => void;
  selectedId?: number;
}

export const EventsModal: React.FC<EventsModalProps> = ({ onSelect, onClose, selectedId }) => {
  const [search, setSearch] = useState('');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useGetMyEventsInfinite();

  // Flatten all pages into one list
  const allEvents: UserEventItem[] = data?.pages.flatMap((p) => p.results) ?? [];

  // Client-side search filter
  const searchLower = search.trim().toLowerCase();
  const events = searchLower
    ? allEvents.filter(
        (ev) =>
          (ev.event_title ?? '').toLowerCase().includes(searchLower) ||
          (ev.location ?? '').toLowerCase().includes(searchLower),
      )
    : allEvents;

  // IntersectionObserver sentinel — triggers load-more near the bottom
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Focus search on mount
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const getEventImage = (ev: UserEventItem): string | undefined => {
    const first = ev.medias?.[0];
    return first?.file_url ?? first?.image ?? undefined;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md">
      <div
        className="w-full sm:max-w-[520px] bg-[#0a0a0a] border border-white/[0.06] rounded-t-[24px] sm:rounded-[24px] overflow-hidden shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[720px]"
        style={{
          animation: 'slideUpFade 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* ── Drag handle (mobile) ── */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* ── Header ── */}
        <div className="flex justify-between items-start px-5 pt-3 pb-3 sm:p-6 sm:pb-4 border-b border-white/[0.04]">
          <div className="flex flex-col gap-0.5 sm:gap-1">
            <h3 className="text-white text-base sm:text-lg font-bold tracking-tight">
              Select Event
            </h3>
            <p className="text-zinc-500 text-xs sm:text-[13px]">
              Choose an event to link with your media
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/[0.04] border border-white/[0.06] text-zinc-400 hover:text-white hover:bg-white/[0.08] hover:border-white/[0.1] transition-all flex items-center justify-center shrink-0 mt-0.5 active:scale-90"
          >
            <FiX size={16} className="sm:hidden" />
            <FiX size={18} className="hidden sm:block" />
          </button>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto no-scrollbar overscroll-contain">
          <div className="p-4 sm:p-5 pt-3 sm:pt-4 flex flex-col gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative flex items-center">
              <div className="absolute left-3.5 sm:left-4 text-zinc-500 pointer-events-none">
                <FiSearch size={16} className="sm:hidden" />
                <FiSearch size={18} className="hidden sm:block" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events..."
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl sm:rounded-2xl pl-10 sm:pl-11 pr-10 h-11 sm:h-12 text-white text-sm sm:text-[15px] font-normal placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/30 focus:bg-white/[0.04] transition-all"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch('');
                    inputRef.current?.focus();
                  }}
                  className="absolute right-3 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/[0.06] text-zinc-500 hover:text-white hover:bg-white/[0.1] transition-all flex items-center justify-center"
                >
                  <FiX size={12} className="sm:hidden" />
                  <FiX size={14} className="hidden sm:block" />
                </button>
              )}
            </div>

            {/* Result count */}
            {searchLower && !isLoading && events.length > 0 && (
              <p className="text-zinc-500 text-[11px] sm:text-xs px-1">
                {events.length} result{events.length !== 1 ? 's' : ''} for "{search.trim()}"
              </p>
            )}

            {isLoading ? (
              <div className="flex flex-col gap-2.5 sm:gap-3 pt-1">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="w-full p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-white/[0.04] bg-white/[0.02] flex items-center gap-3 sm:gap-4"
                  >
                    <div className="w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-lg sm:rounded-xl bg-white/[0.04] animate-pulse shrink-0 overflow-hidden flex items-center justify-center">
                      <img
                        src={MEDIA_FALLBACK_IMAGE_SRC}
                        alt=""
                        className="w-6 h-6 sm:w-8 sm:h-8 object-contain opacity-20"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-2 sm:gap-2.5">
                      <div className="h-3.5 sm:h-4 w-1/2 rounded-md bg-white/[0.04] animate-pulse" />
                      <div className="h-2.5 sm:h-3 w-1/3 rounded-md bg-white/[0.03] animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 sm:py-14 text-center gap-4 sm:gap-5">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center overflow-hidden">
                  <img
                    src={MEDIA_FALLBACK_IMAGE_SRC}
                    alt=""
                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain opacity-40"
                  />
                </div>
                <div className="flex flex-col gap-1 sm:gap-1.5">
                  <p className="text-white text-sm sm:text-base font-semibold">
                    {search ? 'No events found' : 'No events yet'}
                  </p>
                  <p className="text-zinc-500 text-xs sm:text-sm max-w-[240px] sm:max-w-[260px] leading-relaxed">
                    {search
                      ? 'Try a different keyword or check your spelling.'
                      : 'Your events will appear here once you create them.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:gap-2.5 pt-1">
                {events.map((ev) => {
                  const img = getEventImage(ev);
                  const isSelected = selectedId === ev.id;
                  const date = formatDate(ev.start);

                  return (
                    <button
                      key={ev.id}
                      onClick={() =>
                        onSelect({
                          id: ev.id,
                          name: ev.event_title ?? 'Untitled Event',
                          location: ev.location,
                          start: ev.start,
                          image: img,
                        })
                      }
                      className={`group w-full p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border text-left flex items-center gap-3 sm:gap-4 transition-all duration-200 relative overflow-hidden active:scale-[0.99] ${
                        isSelected
                          ? 'bg-blue-500/[0.06] border-blue-500/20 shadow-[0_0_0_1px_rgba(59,130,246,0.1)]'
                          : 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.04] hover:border-white/[0.1] hover:shadow-lg hover:shadow-black/20'
                      }`}
                    >
                      {/* Left accent bar for selected state */}
                      {isSelected && (
                        <div className="absolute left-0 top-2.5 sm:top-3 bottom-2.5 sm:bottom-3 w-[2.5px] sm:w-[3px] rounded-full bg-blue-500" />
                      )}

                      {/* Image */}
                      <img
                        src={img || MEDIA_FALLBACK_IMAGE_SRC}
                        alt=""
                        className={`w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-lg sm:rounded-xl object-cover shrink-0 transition-transform duration-300 ${
                          isSelected ? 'ring-2 ring-blue-500/30' : 'group-hover:scale-[1.02]'
                        }`}
                      />

                      {/* Content */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1 sm:gap-1.5 py-0.5">
                        <h4
                          className={`font-semibold text-sm sm:text-[15px] leading-snug truncate transition-colors ${
                            isSelected ? 'text-blue-400' : 'text-white'
                          }`}
                        >
                          {ev.event_title ?? 'Untitled Event'}
                        </h4>

                        {ev.location && (
                          <div className="flex items-center gap-1.5 text-zinc-500">
                            <FiMapPin size={11} className="shrink-0 mt-[1px] sm:hidden" />
                            <FiMapPin size={12} className="shrink-0 mt-[1px] hidden sm:block" />
                            <p className="text-xs sm:text-[13px] leading-relaxed truncate">
                              {ev.location}
                            </p>
                          </div>
                        )}

                        {date && (
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium px-1.5 sm:px-2 py-0.5 rounded-md ${
                                isSelected
                                  ? 'bg-blue-500/10 text-blue-400'
                                  : 'bg-white/[0.04] text-zinc-500'
                              }`}
                            >
                              <FiCalendar size={9} className="sm:hidden" />
                              <FiCalendar size={10} className="hidden sm:block" />
                              {date}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Selection indicator */}
                      <div
                        className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 shrink-0 transition-all duration-200 flex items-center justify-center ${
                          isSelected
                            ? 'bg-blue-500 border-blue-500 scale-110'
                            : 'border-zinc-700 group-hover:border-zinc-500'
                        }`}
                      >
                        {isSelected && (
                          <FiCheck size={12} className="text-white sm:hidden" strokeWidth={3} />
                        )}
                        {isSelected && (
                          <FiCheck
                            size={14}
                            className="text-white hidden sm:block"
                            strokeWidth={3}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Infinite-scroll sentinel */}
                <div ref={sentinelRef} className="py-2" />
                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <div className="w-5 h-5 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};
